/**
 * Reusable Data Table Component
 * Generic table component that can be used across the system
 * Supports pagination, actions, responsive design, and RTL
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  hiddenOn?: 'mobile' | 'tablet' | 'desktop';
  className?: string;
  render?: (item: T) => React.ReactNode;
}

export interface TableAction<T> {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: (item: T) => void;
  variant?: 'default' | 'destructive';
  separator?: boolean; // Add separator before this action
}

export interface PaginationInfo {
  current: number;
  total: number;
  count: number;
  itemsPerPage: number;
}

export interface DataTableProps<T> {
  // Data
  data: T[];
  columns: TableColumn<T>[];

  // Loading/Error states
  isLoading?: boolean;
  error?: Error | null;
  loadingMessage?: string;
  errorMessage?: string;
  showLoadingIcon?: boolean;

  // Pagination
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;

  // Actions
  actions?: TableAction<T>[];

  // Empty state
  emptyMessage?: string;

  // Accessibility
  tableCaption?: string;

  // Custom classes
  className?: string;
  tableClassName?: string;
}

// ============================================================================
// Responsive Breakpoints
// ============================================================================

const getHiddenClasses = (hiddenOn: string | undefined): string => {
  switch (hiddenOn) {
    case 'mobile':
      return 'hidden sm:table-cell';
    case 'tablet':
      return 'hidden md:table-cell';
    case 'desktop':
      return 'table-cell md:hidden';
    default:
      return '';
  }
};

// ============================================================================
// Data Table Component
// ============================================================================

export function DataTable<
  T extends { id?: string | number } & Record<string, unknown>,
>({
  data,
  columns,
  isLoading = false,
  error = null,
  loadingMessage,
  errorMessage,
  showLoadingIcon = true,
  pagination,
  onPageChange,
  actions = [],
  emptyMessage,
  tableCaption,
  className,
  tableClassName,
}: DataTableProps<T>) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // ============================================================================
  // Render cell content
  // ============================================================================

  const renderCell = (item: T, column: TableColumn<T>) => {
    if (column.render) {
      return column.render(item);
    }

    const value = item[column.key as keyof T];

    // Handle different data types
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground">—</span>;
    }

    if (typeof value === 'boolean') {
      return (
        <Badge
          variant={value ? 'secondary' : 'destructive'}
          className="text-xs"
        >
          {value
            ? t('common.active', 'Active')
            : t('common.inactive', 'Inactive')}
        </Badge>
      );
    }

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    return String(value);
  };

  // ============================================================================
  // Render actions dropdown
  // ============================================================================

  const renderActions = (item: T) => {
    if (actions.length === 0) return null;

    return (
      <DropdownMenu dir={isRTL ? 'ltr' : 'rtl'}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <span className="sr-only">{t('common.openMenu', 'Open menu')}</span>
            <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={isRTL ? 'start' : 'end'}
          sideOffset={5}
          collisionPadding={20}
          className="w-40"
        >
          <DropdownMenuLabel className="text-xs sm:text-sm">
            {t('common.actions', 'Actions')}
          </DropdownMenuLabel>
          {actions.map((action, index) => (
            <div key={index}>
              {action.separator && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() => action.onClick(item)}
                className={cn(
                  'text-xs sm:text-sm',
                  action.variant === 'destructive' && 'text-destructive'
                )}
              >
                <action.icon
                  className={cn(
                    'h-3 w-3 sm:h-4 sm:w-4',
                    isRTL ? 'ml-2' : 'mr-2'
                  )}
                />
                {action.label}
              </DropdownMenuItem>
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  // ============================================================================
  // Render pagination
  // ============================================================================

  const renderPagination = () => {
    if (!pagination || !onPageChange) return null;

    const { current, total, count, itemsPerPage } = pagination;

    if (total <= 1) return null;

    return (
      <Card className="mt-4">
        <CardContent className="py-3 sm:py-4 px-3 sm:px-4">
          <div className="flex items-center justify-center">
            <div
              className={cn(
                'flex items-center justify-center flex-wrap gap-2 sm:gap-3 w-full',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              {/* Previous Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(current - 1)}
                disabled={current === 1}
                className="h-10 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm min-w-fit"
              >
                {isRTL ? (
                  <>
                    <span className="hidden sm:inline">
                      {t('pagination.previous', 'Previous')}
                    </span>
                    <span className="sm:hidden">Prev</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">
                      {t('pagination.previous', 'Previous')}
                    </span>
                    <span className="sm:hidden">Prev</span>
                  </>
                )}
              </Button>

              {/* Page numbers - Show on sm and up */}
              <div className="hidden sm:flex items-center gap-1">
                {Array.from({ length: Math.min(5, total) }, (_, i) => {
                  let pageNum;
                  if (total <= 5) {
                    pageNum = i + 1;
                  } else if (current <= 3) {
                    pageNum = i + 1;
                  } else if (current >= total - 2) {
                    pageNum = total - 4 + i;
                  } else {
                    pageNum = current - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={current === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageChange(pageNum)}
                      className="w-9 h-9 p-0 text-xs sm:text-sm"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              {/* Page info for mobile */}
              <div className="sm:hidden text-xs sm:text-sm text-muted-foreground px-2 py-1 bg-muted/50 rounded min-w-[60px] text-center">
                {current}/{total}
              </div>

              {/* Next Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(current + 1)}
                disabled={current === total}
                className="h-10 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm min-w-fit"
              >
                {isRTL ? (
                  <>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">
                      {t('pagination.next', 'Next')}
                    </span>
                    <span className="sm:hidden">Next</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      {t('pagination.next', 'Next')}
                    </span>
                    <span className="sm:hidden">Next</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Results info */}
          <div className="mt-3 text-center">
            <p className="text-xs text-muted-foreground">
              {t(
                'common.showingResults',
                'Showing {{start}}-{{end}} of {{total}} results',
                {
                  start: (current - 1) * itemsPerPage + 1,
                  end: Math.min(current * itemsPerPage, count),
                  total: count,
                }
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ============================================================================
  // Render loading state
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {loadingMessage || t('common.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render error state
  // ============================================================================

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <div>
            <p className="font-medium text-destructive">
              {errorMessage || t('common.error', 'An error occurred')}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {error.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Render empty state
  // ============================================================================

  if (data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {emptyMessage || t('common.noData', 'No data available')}
        </p>
      </div>
    );
  }

  // ============================================================================
  // Render table
  // ============================================================================

  return (
    <div className={cn('space-y-4', className)}>
      <div className="relative">
        <div className="overflow-x-auto">
          <Table className={tableClassName}>
          {tableCaption && (
            <caption className="sr-only">{tableCaption}</caption>
          )}
          <TableHeader>
            <TableRow>
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={cn(
                    'text-xs sm:text-sm',
                    getHiddenClasses(column.hiddenOn),
                    isRTL ? 'text-right' : '',
                    column.className
                  )}
                >
                  {column.label}
                </TableHead>
              ))}
              {actions.length > 0 && (
                <TableHead
                  className={cn(
                    'text-xs sm:text-sm w-16',
                    isRTL ? 'text-left' : 'text-right'
                  )}
                >
                  <div className="flex items-center justify-end gap-2">
                    {showLoadingIcon && isLoading && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    <span>{t('common.actions', 'Actions')}</span>
                  </div>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, rowIndex) => (
              <TableRow key={item.id || rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    className={cn(
                      'py-3',
                      getHiddenClasses(column.hiddenOn),
                      column.className
                    )}
                  >
                    {renderCell(item, column)}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell
                    className={cn('py-3', isRTL ? 'text-left' : 'text-right')}
                  >
                    {renderActions(item)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {renderPagination()}
    </div>
  );
}
